// Keeps long-lived tabs on the current release.
//
// After a deploy, a tab opened earlier still runs the old bundle. Its lazy
// route chunks can disappear from S3 and the page would hang on a spinner
// until the user pressed Ctrl+R. Two guards:
//   1. watchForNewVersion: poll asset-manifest.json; once a new build is out,
//      the next in-app navigation becomes a full page load of the new build.
//   2. reloadOnChunkError: if a chunk still fails to load, reload once.

const MANIFEST_URL = '/asset-manifest.json';
const POLL_MS = 5 * 60 * 1000;
const RELOAD_GUARD_KEY = '2way.chunkReloadAt';
const RELOAD_GUARD_MS = 60 * 1000;

// The main bundle this tab is running, taken from its own <script> tag.
const currentMain = () => {
    const script = [...document.scripts].find(s => /\/static\/js\/main\.[0-9a-f]+\.chunk\.js$/.test(s.src));
    return script ? new URL(script.src).pathname : null;
};

const fetchLatestMain = async () => {
    const response = await fetch(MANIFEST_URL, {cache: 'no-store', credentials: 'same-origin'});
    if (!response.ok) return null;
    const manifest = await response.json();
    return (manifest.files && manifest.files['main.js']) || null;
};

export const isChunkLoadError = error =>
    !!error && (error.name === 'ChunkLoadError' || /Loading (CSS )?chunk [\w-]+ failed/i.test(error.message || ''));

// Reload at most once a minute, so a real outage cannot cause a reload loop.
// If sessionStorage is unavailable the guard cannot be kept, so don't reload.
export const reloadOnce = () => {
    try {
        const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY)) || 0;
        if (Date.now() - last < RELOAD_GUARD_MS) return false;
        sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
    } catch (e) {
        return false;
    }
    window.location.reload();
    return true;
};

export const watchForNewVersion = history => {
    // Development server: no hashed main bundle, nothing to compare.
    const running = currentMain();
    if (!running || process.env.NODE_ENV !== 'production') return;

    let updateReady = false;

    const check = async () => {
        if (updateReady) return;
        try {
            const latest = await fetchLatestMain();
            if (latest && latest !== running) updateReady = true;
        } catch (e) {
            // offline or blocked: try again next time
        }
    };

    setInterval(check, POLL_MS);
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') check();
    });

    // The URL has already changed when this fires, so reloading lands the
    // user on the page they clicked, now served by the new build.
    history.listen(() => {
        if (updateReady) window.location.reload();
    });
};

export const installChunkErrorReload = () => {
    window.addEventListener('unhandledrejection', event => {
        if (isChunkLoadError(event.reason)) reloadOnce();
    });
};
